import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type DecisionSheetView, type TaskView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { DatePicker } from '../../../components/ui/date-picker';
import { Dialog, DialogApply } from '../../../components/ui/dialog';
import { GlassSegmented } from '../../../components/ui/glass-segmented';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { ErrorState, Skeleton } from '../../../components/ui/states';
import { errorMessage } from '../../../lib/error-message';
import { cn } from '../../../lib/utils';
import { boardApi, boardKeys } from '../../board/api';
import { taskFormApi, taskFormKeys } from '../../tasks/api';
import { ALL_SPRINTS } from '../../tasks/components/sprint-picker';
import { decisionSheetApi, decisionSheetKeys } from '../api';

type Tab = 'create' | 'link';
const TABS: readonly Tab[] = ['create', 'link'];

const SELECT_CLASS = 'h-9 w-full rounded-DEFAULT border border-input bg-background px-2 text-sm';

/** Link to the sheet page, written into the new task's description. */
export function sheetPageUrl(view: Pick<DecisionSheetView, 'id' | 'projectId'>): string {
  const search = new URLSearchParams({
    projectId: view.projectId,
    tab: 'overview',
    sheet: view.id,
  });
  return `${window.location.origin}/projects?${search.toString()}`;
}

/**
 * "Giao việc trả lời" — APM-DSX-d, plan §11.2.
 *
 * ## Hai bước, hai transaction
 *
 * Task thuộc module `tasks`, sổ thuộc `decision-sheets` ⇒ tạo task bằng
 * endpoint có sẵn (`POST /projects/:id/tasks`) rồi mới
 * `POST /decision-sheets/:id/tasks`. Bước hai hỏng thì task **đã có**: hộp
 * giữ nguyên task vừa tạo, nói rõ, và nút "Thử gắn lại" chỉ gọi lại bước hai —
 * bấm lại "Giao việc" mà tạo thêm một task là đẻ task trùng.
 *
 * "Gắn việc có sẵn" đi đúng bước hai, cho task đã có (kể cả task vừa tạo ở
 * lượt trước mà bước nối hỏng rồi đóng hộp).
 */
export function AssignAnswerTaskDialog({
  view,
  onClose,
}: {
  readonly view: DecisionSheetView;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = view.projectId;
  const [tab, setTab] = useState<Tab>('create');
  const [title, setTitle] = useState(() =>
    t('decisionSheet.assign.suggestedTitle', { title: view.title }),
  );
  const [assigneeId, setAssigneeId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [taskTypeId, setTaskTypeId] = useState('');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  /** Task created by step one whose link (step two) failed — never created twice. */
  const [orphan, setOrphan] = useState<TaskView | null>(null);
  const [linkError, setLinkError] = useState<unknown>(null);

  const members = useQuery({
    queryKey: taskFormKeys.members(projectId),
    queryFn: () => taskFormApi.members(projectId),
  });
  const types = useQuery({
    queryKey: taskFormKeys.taskTypes(projectId),
    queryFn: () => taskFormApi.taskTypes(projectId),
  });
  const board = useQuery({
    // Same cache cell as the Tasks tab in "all sprints" mode.
    queryKey: boardKeys.board(projectId, ALL_SPRINTS),
    queryFn: () => boardApi.board(projectId, undefined, true),
    enabled: tab === 'link',
  });
  const typeId = taskTypeId || types.data?.[0]?.id || '';

  const linkedIds = useMemo(() => new Set((view.tasks ?? []).map((task) => task.taskId)), [view]);
  const candidates = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('vi');
    return (board.data?.columns ?? [])
      .flatMap((column) => column.tasks)
      .filter((task) => !linkedIds.has(task.id))
      .filter(
        (task) =>
          !needle ||
          task.key.toLocaleLowerCase('vi').includes(needle) ||
          task.title.toLocaleLowerCase('vi').includes(needle),
      )
      .slice(0, 50);
  }, [board.data, linkedIds, query]);

  const finish = (code: string, addedDecider: boolean, assigneeName: string | null): void => {
    void queryClient.invalidateQueries({ queryKey: ['decision-sheet', view.id] });
    void queryClient.invalidateQueries({ queryKey: decisionSheetKeys.list(projectId) });
    void queryClient.invalidateQueries({ queryKey: ['board', projectId] });
    toast.success(
      addedDecider && assigneeName
        ? t('decisionSheet.assign.doneDecider', { code, name: assigneeName })
        : t('decisionSheet.assign.done', { code }),
    );
    onClose();
  };

  /** Step two only — shared by "Assign", "Try again" and "Attach existing". */
  const link = useMutation({
    mutationFn: (task: Pick<TaskView, 'id' | 'key' | 'assigneeName'>) =>
      decisionSheetApi.linkTask(view.id, task.id),
    onSuccess: (result, task) => finish(task.key, result.addedDecider, task.assigneeName),
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const create = useMutation({
    mutationFn: async () => {
      const { task } = await taskFormApi.create(projectId, {
        title: title.trim(),
        taskTypeId: typeId,
        description: t('decisionSheet.assign.description', {
          title: view.title,
          url: sheetPageUrl(view),
        }),
        ...(assigneeId ? { assigneeId } : {}),
        ...(dueDate ? { dueDate } : {}),
      });
      // The task exists from here on: a failing link must not lose it.
      setOrphan(task);
      void queryClient.invalidateQueries({ queryKey: ['board', projectId] });
      try {
        const result = await decisionSheetApi.linkTask(view.id, task.id);
        return { task, result };
      } catch (error) {
        setLinkError(error);
        return null;
      }
    },
    onSuccess: (outcome) => {
      if (outcome) finish(outcome.task.key, outcome.result.addedDecider, outcome.task.assigneeName);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const retry = (): void => {
    if (!orphan) return;
    setLinkError(null);
    link.mutate(orphan, { onError: (error) => setLinkError(error) });
  };

  const busy = create.isPending || link.isPending;
  const canCreate = !orphan && title.trim() !== '' && typeId !== '' && !busy;
  const pickedTask = candidates.find((task) => task.id === picked) ?? null;

  return (
    <Dialog label={t('decisionSheet.assign.title')} onClose={onClose} fixedHeight>
      {tab === 'create' ? (
        <DialogApply
          label={t('decisionSheet.assign.submit')}
          disabled={!canCreate}
          pending={create.isPending}
          onClick={() => create.mutate()}
        />
      ) : (
        <DialogApply
          label={t('decisionSheet.assign.linkSubmit')}
          disabled={!pickedTask || busy}
          pending={link.isPending}
          onClick={() => pickedTask && link.mutate(pickedTask)}
        />
      )}
      <h2 className="text-lg font-semibold">{t('decisionSheet.assign.title')}</h2>
      <p className="text-sm text-muted-foreground">{t('decisionSheet.assign.hint')}</p>

      <GlassSegmented
        size="sm"
        ariaLabel={t('decisionSheet.assign.title')}
        value={tab}
        onChange={setTab}
        disabled={orphan !== null}
        options={TABS.map((value) => ({
          value,
          label: t(`decisionSheet.assign.tabs.${value}`),
        }))}
      />

      {orphan && linkError !== null ? (
        <div
          role="alert"
          className="flex flex-col gap-2 rounded-xl border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm"
          data-testid="decision-assign-link-failed"
        >
          <p>
            {t('decisionSheet.assign.linkFailed', {
              code: orphan.key,
              reason: errorMessage(linkError, t),
            })}
          </p>
          <Button size="sm" variant="outline" className="self-start" onClick={retry}>
            {t('decisionSheet.assign.retryLink')}
          </Button>
        </div>
      ) : null}

      {tab === 'create' ? (
        members.isPending || types.isPending ? (
          <Skeleton className="h-40" />
        ) : members.isError || types.isError ? (
          <ErrorState
            message={errorMessage(members.error ?? types.error, t)}
            onRetry={() => {
              void members.refetch();
              void types.refetch();
            }}
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="decision-assign-title">{t('decisionSheet.assign.taskTitle')}</Label>
              <Input
                id="decision-assign-title"
                value={title}
                maxLength={500}
                disabled={orphan !== null}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor="decision-assign-assignee">{t('decisionSheet.assign.assignee')}</Label>
              <select
                id="decision-assign-assignee"
                value={assigneeId}
                disabled={orphan !== null}
                onChange={(event) => setAssigneeId(event.target.value)}
                className={SELECT_CLASS}
              >
                <option value="">{t('decisionSheet.assign.assigneeNone')}</option>
                {members.data.map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor="decision-assign-due">{t('decisionSheet.assign.due')}</Label>
              <DatePicker
                id="decision-assign-due"
                value={dueDate}
                disabled={orphan !== null}
                onChange={setDueDate}
              />
            </div>
            {types.data.length === 0 ? (
              <p role="alert" className="text-xs text-destructive sm:col-span-2">
                {t('decisionSheet.assign.noTypes')}
              </p>
            ) : types.data.length > 1 ? (
              <div className="flex min-w-0 flex-col gap-1.5">
                <Label htmlFor="decision-assign-type">{t('decisionSheet.assign.taskType')}</Label>
                <select
                  id="decision-assign-type"
                  value={typeId}
                  disabled={orphan !== null}
                  onChange={(event) => setTaskTypeId(event.target.value)}
                  className={SELECT_CLASS}
                >
                  {types.data.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
        )
      ) : (
        <div className="flex min-h-0 flex-col gap-2">
          <Input
            type="search"
            value={query}
            placeholder={t('decisionSheet.assign.search')}
            aria-label={t('decisionSheet.assign.search')}
            onChange={(event) => setQuery(event.target.value)}
          />
          {board.isPending ? (
            <Skeleton className="h-40" />
          ) : board.isError ? (
            <ErrorState
              message={errorMessage(board.error, t)}
              onRetry={() => void board.refetch()}
            />
          ) : candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('decisionSheet.assign.noTasks')}</p>
          ) : (
            <ul
              role="listbox"
              aria-label={t('decisionSheet.assign.tabs.link')}
              className="flex flex-col divide-y divide-border overflow-y-auto rounded-xl border border-border"
            >
              {candidates.map((task) => (
                <li
                  key={task.id}
                  role="option"
                  aria-selected={picked === task.id}
                  tabIndex={0}
                  onClick={() => setPicked(task.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setPicked(task.id);
                    }
                  }}
                  className={cn(
                    'flex cursor-pointer flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-2 text-sm',
                    picked === task.id ? 'bg-primary/10' : 'hover:bg-muted/50',
                  )}
                >
                  <span className="font-mono text-xs text-muted-foreground">{task.key}</span>
                  <span className="min-w-0 flex-1 truncate">{task.title}</span>
                  <span className="text-xs text-muted-foreground">
                    {task.assigneeName ?? t('decisionSheet.assign.assigneeNone')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Dialog>
  );
}
